// src/services/azureProjectPhasesService.js
// Azure Table Storage Service für Projektphasen

const AZURE_CONFIG = {
  storageAccount: "mfprojects",
  tableName: "projectPhases",
  sasToken:
    "sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D",
};

// RowKey erstellen: ProjectNumber_PhaseId
const createRowKey = (projectNumber, phaseId) => {
  return `${projectNumber}_${phaseId}`;
};

const generatePhaseId = () => {
  return Date.now().toString() + "_" + Math.random().toString(36).substr(2, 9);
};

// Phasen für ein Projekt laden
export const getProjectPhases = async (projectNumber) => {
  try {
    let allPhases = [];
    let nextPartitionKey = null;
    let nextRowKey = null;
    const filter = `$filter=ProjectNumber eq '${projectNumber}'`;

    do {
      let url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&${filter}`;

      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      allPhases = allPhases.concat(data.value || []);

      nextPartitionKey = response.headers.get(
        "x-ms-continuation-NextPartitionKey"
      );
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return allPhases.map((item) => ({
      id: item.PhaseId,
      projectNumber: item.ProjectNumber,
      name: item.PhaseName,
      startDate: item.StartDate,
      endDate: item.EndDate,
      budget: item.Budget || 0,
      externalBudget: item.ExternalBudget || 0,
      hourlyExternalRate: item.HourlyExternalRate || null,
      personProducts: item.PersonProducts ? JSON.parse(item.PersonProducts) : {},
      partnerBudgets: item.PartnerBudgets ? JSON.parse(item.PartnerBudgets) : {},
      milestones: item.Milestones ? JSON.parse(item.Milestones) : [],
      localOnly: item.LocalOnly === true || item.LocalOnly === "true",
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    }));
  } catch (error) {
    console.error("Get Project Phases Error:", error);
    return [];
  }
};

// Load all phases across all projects (single query, grouped by projectNumber)
export const getAllPhases = async () => {
  try {
    let allPhases = [];
    let nextPartitionKey = null;
    let nextRowKey = null;

    do {
      let url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}`;
      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) throw new Error(`Azure Table Error: ${response.status}`);

      const data = await response.json();
      allPhases = allPhases.concat(data.value || []);

      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return allPhases.map((item) => ({
      id: item.PhaseId,
      projectNumber: item.ProjectNumber,
      name: item.PhaseName,
      startDate: item.StartDate,
      endDate: item.EndDate,
      localOnly: item.LocalOnly === true || item.LocalOnly === "true",
    }));
  } catch (error) {
    console.error("Get All Phases Error:", error);
    return [];
  }
};

// Phase speichern/aktualisieren
export const savePhase = async (projectNumber, phaseData) => {
  try {
    const phaseId = phaseData.id || generatePhaseId();
    const rowKey = createRowKey(projectNumber, phaseId);
    const now = new Date().toISOString();

    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='phase',RowKey='${rowKey}')?${AZURE_CONFIG.sasToken}`;

    const entity = {
      PartitionKey: "phase",
      RowKey: rowKey,
      ProjectNumber: projectNumber,
      PhaseId: phaseId,
      PhaseName: phaseData.name,
      StartDate: phaseData.startDate,
      EndDate: phaseData.endDate,
      Budget: parseFloat(phaseData.budget) || 0,
      ExternalBudget: parseFloat(phaseData.externalBudget) || 0,
      HourlyExternalRate: phaseData.hourlyExternalRate ? parseFloat(phaseData.hourlyExternalRate) : null,
      PersonProducts: phaseData.personProducts ? JSON.stringify(phaseData.personProducts) : "{}",
      PartnerBudgets: phaseData.partnerBudgets ? JSON.stringify(phaseData.partnerBudgets) : "{}",
      Milestones: phaseData.milestones ? JSON.stringify(phaseData.milestones) : "[]",
      LocalOnly: phaseData.localOnly || false,
      CreatedAt: phaseData.createdAt || now,
      UpdatedAt: now,
    };

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json;odata=nometadata",
      },
      body: JSON.stringify(entity),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure Save Error: ${response.status}`);
    }

    return { ...phaseData, id: phaseId };
  } catch (error) {
    console.error("Save Phase Error:", error);
    throw error;
  }
};

// Mehrere Phasen auf einmal speichern
export const saveBulkPhases = async (projectNumber, phases) => {
  try {
    const promises = phases.map((phase) => savePhase(projectNumber, phase));
    const results = await Promise.all(promises);
    return results;
  } catch (error) {
    console.error("Save Bulk Phases Error:", error);
    throw error;
  }
};

// Phase löschen
export const deletePhase = async (projectNumber, phaseId) => {
  try {
    const rowKey = createRowKey(projectNumber, phaseId);
    const url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}(PartitionKey='phase',RowKey='${rowKey}')?${AZURE_CONFIG.sasToken}`;

    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "If-Match": "*",
      },
    });

    if (!response.ok && response.status !== 404) {
      const errorText = await response.text();
      throw new Error(`Azure Delete Error: ${response.status}`);
    }

    return true;
  } catch (error) {
    console.error("Delete Phase Error:", error);
    throw error;
  }
};

// Alle Phasen für ein Projekt löschen
export const deleteAllProjectPhases = async (projectNumber) => {
  try {
    const phases = await getProjectPhases(projectNumber);

    const promises = phases.map((phase) =>
      deletePhase(projectNumber, phase.id)
    );

    await Promise.all(promises);
    return true;
  } catch (error) {
    console.error("Delete All Project Phases Error:", error);
    throw error;
  }
};

// Alle Phasen mit localOnly-Flag laden (projektübergreifend)
export const getLocalOnlyPhases = async () => {
  try {
    let allPhases = [];
    let nextPartitionKey = null;
    let nextRowKey = null;
    const filter = `$filter=LocalOnly eq true`;

    do {
      let url = `https://${AZURE_CONFIG.storageAccount}.table.core.windows.net/${AZURE_CONFIG.tableName}?${AZURE_CONFIG.sasToken}&${filter}`;

      if (nextPartitionKey && nextRowKey) {
        url += `&NextPartitionKey=${encodeURIComponent(nextPartitionKey)}`;
        url += `&NextRowKey=${encodeURIComponent(nextRowKey)}`;
      }

      const response = await fetch(url, {
        headers: { Accept: "application/json;odata=nometadata" },
      });

      if (!response.ok) {
        throw new Error(`Azure Table Error: ${response.status}`);
      }

      const data = await response.json();
      allPhases = allPhases.concat(data.value || []);

      nextPartitionKey = response.headers.get("x-ms-continuation-NextPartitionKey");
      nextRowKey = response.headers.get("x-ms-continuation-NextRowKey");
    } while (nextPartitionKey && nextRowKey);

    return allPhases.map((item) => ({
      id: item.PhaseId,
      projectNumber: item.ProjectNumber,
      name: item.PhaseName,
      startDate: item.StartDate,
      endDate: item.EndDate,
      budget: item.Budget || 0,
      externalBudget: item.ExternalBudget || 0,
      localOnly: true,
      createdAt: item.CreatedAt,
      updatedAt: item.UpdatedAt,
    }));
  } catch (error) {
    console.error("Get LocalOnly Phases Error:", error);
    return [];
  }
};

export const updateConfig = (newConfig) => {
  Object.assign(AZURE_CONFIG, newConfig);
};
